<template>
  <div class="medium-3 bg-white contact--panel">
    <div class="ctx-tabs">
      <button
        class="ctx-tab"
        :class="{ active: activeTab === 'details' }"
        @click="activeTab = 'details'"
      >
        <svg class="ctx-tab-ic" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
        {{ $t('CONVERSATION_SIDEBAR.TABS.DETAILS') }}
      </button>
      <button
        v-if="isCopilotEnabled"
        class="ctx-tab ctx-tab--ai"
        :class="{ active: activeTab === 'ai' }"
        @click="activeTab = 'ai'"
      >
        <svg class="ctx-tab-ic" viewBox="0 0 24 24">
          <path
            d="M12 3 13.9 8.6 20 9.2 15.5 13.1 16.8 19 12 15.8 7.2 19 8.5 13.1 4 9.2 10.1 8.6 12 3Z"
          />
        </svg>
        {{ $t('CONVERSATION_SIDEBAR.TABS.COPILOT') }}
      </button>
      <button
        class="ctx-collapse"
        :title="$t('CONVERSATION_SIDEBAR.COLLAPSE_PANEL')"
        @click="onPanelToggle"
      >
        <svg class="ctx-tab-ic" viewBox="0 0 24 24">
          <path d="M9 6l6 6-6 6" />
        </svg>
      </button>
    </div>
    <copilot-panel
      v-if="isCopilotEnabled"
      v-show="activeTab === 'ai'"
      :conversation-id="conversationId"
      :last-message-id="copilotLastMessageId"
    />
    <contact-info
      v-show="activeTab === 'details'"
      :conversation="currentChat"
      :contact="contact"
      :channel-type="channelType"
      :show-close-button="false"
      @toggle-panel="onPanelToggle"
    />
    <draggable
      v-show="activeTab === 'details'"
      :list="conversationSidebarItems"
      :disabled="!dragEnabled"
      animation="200"
      class="list-group"
      ghost-class="ghost"
      handle=".drag-handle"
      @start="dragging = true"
      @end="onDragEnd"
    >
      <transition-group>
        <div
          v-for="element in conversationSidebarItems"
          :key="element.name"
          class="list-group-item"
        >
          <div
            v-if="element.name === 'conversation_actions'"
            class="conversation--actions"
          >
            <accordion-item
              proto-icon="assignment"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')"
              :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
              @click="
                value => toggleSidebarUIState('is_conv_actions_open', value)
              "
            >
              <conversation-action
                :conversation-id="conversationId"
                :inbox-id="inboxId"
                :channel-type="channelType"
              />
            </accordion-item>
          </div>
          <div
            v-else-if="element.name === 'conversation_participants'"
            class="conversation--actions"
          >
            <accordion-item
              proto-icon="people"
              :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
              :is-open="isContactSidebarItemOpen('is_conv_participants_open')"
              @click="
                value =>
                  toggleSidebarUIState('is_conv_participants_open', value)
              "
            >
              <conversation-participant
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </accordion-item>
          </div>
          <div
            v-else-if="
              element.name === 'conversation_info' && !currentChat.group
            "
          >
            <accordion-item
              proto-icon="info"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
              :is-open="isContactSidebarItemOpen('is_conv_details_open')"
              compact
              @click="
                value => toggleSidebarUIState('is_conv_details_open', value)
              "
            >
              <conversation-info
                :conversation-attributes="conversationAdditionalAttributes"
                :contact-attributes="contactAdditionalAttributes"
              />
            </accordion-item>
          </div>
          <div
            v-else-if="
              element.name === 'contact_attributes' && !currentChat.group
            "
          >
            <accordion-item
              proto-icon="list"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
              :is-open="isContactSidebarItemOpen('is_contact_attributes_open')"
              compact
              @click="
                value =>
                  toggleSidebarUIState('is_contact_attributes_open', value)
              "
            >
              <custom-attributes
                v-if="!currentChat.group"
                attribute-type="contact_attribute"
                attribute-class="conversation--attribute"
                class="even"
                :contact-id="contact.id"
              />
              <custom-attribute-selector
                attribute-type="contact_attribute"
                :contact-id="contact.id"
              />
            </accordion-item>
          </div>
          <div v-else-if="element.name === 'workplace'" >
            <accordion-item
              proto-icon="grid"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.GROUP_ATTRIBUTES')"
              :is-open="isContactSidebarItemOpen('is_workplace_open')"
              compact
              class="group--container"
              @click="
                value =>
                  toggleSidebarUIState('is_workplace_open', value)
              "
            >
              <spinner v-if="isLoadingGroupAttributes" />
              <div v-else class="group--attribute">
                <custom-multiselect-dropdown
          :options="groupAttributeData"
          :selected-item="workplaceSelected"
          :multiselector-title="$t('CONVERSATION_SIDEBAR.ACCORDION.GROUP_ATTRIBUTES')"
          :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
          :no-search-result="$t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.GROUP')"
          :input-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.GROUP')"
          @click="onClickAssignWorkplace"
        />
              </div>
            </accordion-item>
          </div>
          <div
            v-else-if="element.name === 'contact_wallet' && !currentChat.group"
            class="conversation--actions"
          >
            <accordion-item
              proto-icon="wallet"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_WALLET')"
              :is-open="isContactSidebarItemOpen('is_contact_wallet_open')"
              @click="
                value => toggleSidebarUIState('is_contact_wallet_open', value)
              "
            >
              <contact-wallet :contact="contact" />
            </accordion-item>
          </div>
          <div
            v-else-if="
              element.name === 'previous_conversation' && !currentChat.group
            "
          >
            <accordion-item
              v-if="contact.id"
              proto-icon="history"
              :title="
                $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')
              "
              :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
              @click="
                value => toggleSidebarUIState('is_previous_conv_open', value)
              "
            >
              <contact-conversations
                :contact-id="contact.id"
                :conversation-id="conversationId"
              />
            </accordion-item>
          </div>

          <div
            v-else-if="
              element.name === 'conversations_wise'
            "
          >
            <accordion-item
              v-if="contact.id"
              proto-icon="chat"
              :title="
                $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION_WISE')
              "
              :is-open="isContactSidebarItemOpen('is_wise_conv_open')"
              @click="
                value => toggleSidebarUIState('is_wise_conv_open', value)
              "
            >
              <contact-wise-conversations
                :contact-id="contact.id"
                :account-id="currentAccountId"
              />
            </accordion-item>
          </div>

          <div v-else-if="element.name === 'tickets' && enableCrm">
            <accordion-item
              v-if="contact.id"
              proto-icon="ticket"
              :title="$t('TICKET.TITLE')"
              :is-open="isContactSidebarItemOpen('is_previous_tickets_open')"
              @click="
                value => toggleSidebarUIState('is_previous_tickets_open', value)
              "
            >
              <list-tickets :contact="contact" />
            </accordion-item>
          </div>
          <woot-feature-toggle
            v-else-if="element.name === 'macros' && !currentChat.group"
            feature-key="macros"
          >
            <accordion-item
              proto-icon="bolt"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
              :is-open="isContactSidebarItemOpen('is_macro_open')"
              compact
              @click="value => toggleSidebarUIState('is_macro_open', value)"
            >
              <macros-list :conversation-id="conversationId" />
            </accordion-item>
          </woot-feature-toggle>
        </div>
      </transition-group>
    </draggable>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import AccordionItem from 'dashboard/components/Accordion/AccordionItem';
import ContactConversations from './ContactConversations.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ContactInfo from './contact/ContactInfo';
import ConversationInfo from './ConversationInfo';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import CustomAttributeSelector from './customAttributes/CustomAttributeSelector.vue';
import ContactWallet from './ContactWallet.vue';
import draggable from 'vuedraggable';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import MacrosList from './Macros/List';
import ListTickets from './contact/ListTickets.vue';
import CustomMultiselectDropdown from 'shared/components/ui/CustomMultiselectDropdown.vue';
import ContactWiseConversations from './ContactWiseConversations.vue';
import Spinner from 'shared/components/Spinner';
import CopilotPanel from './CopilotPanel.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';

export default {
  components: {
    AccordionItem,
    CopilotPanel,
    ContactConversations,
    ContactInfo,
    ConversationInfo,
    CustomAttributes,
    CustomAttributeSelector,
    ConversationAction,
    ConversationParticipant,
    ContactWallet,
    draggable,
    MacrosList,
    ListTickets,
    CustomMultiselectDropdown,
    ContactWiseConversations,
    Spinner,
  },
  mixins: [alertMixin, uiSettingsMixin],
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
    inboxId: {
      type: Number,
      default: undefined,
    },
    onToggle: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      dragEnabled: true,
      conversationSidebarItems: [],
      dragging: false,
      activeTab: 'details',
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      uiFlags: 'inboxAssignableAgents/getUIFlags',
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
      attributeUiFlags: 'attributes/getUIFlags',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    // Copiloto IA liberado por conta (feature flag 'copilot', Super Admin).
    isCopilotEnabled() {
      return this.isFeatureEnabledonAccount(this.currentAccountId, 'copilot');
    },
    // Id da última mensagem real (não-atividade) — reativo às mensagens novas
    // via websocket; usado pelo Copiloto para atualizar o sentimento ao vivo.
    copilotLastMessageId() {
      const messages = this.currentChat?.messages || [];
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        const type = messages[i].message_type;
        if (type === 0 || type === 1) return messages[i].id;
      }
      return null;
    },
    groupAttributeData() {
      return this.$store.getters['attributes/getGroupAttributesList'];
    },
    isLoadingGroupAttributes() {
      return (
        this.attributeUiFlags.isFetching && !this.groupAttributeData.length
      );
    },
    workplaceSelected() {
    if (this.groupAttributeData) {
      return this.groupAttributeData.find(attribute => this.contact.workplace === attribute.id);
    }
    return null;
  },
    conversationAdditionalAttributes() {
      return this.currentConversationMetaData.additional_attributes || {};
    },
    channelType() {
      return this.currentChat.meta?.channel;
    },
    contact() {
      if (
        this.currentChat?.meta?.assignee?.id !== this.currentUser?.id &&
        (this?.currentChat?.channel === 'Channel::Internal' ||
          this?.currentChat?.meta?.channel === 'Channel::Internal')
      ) {
        return this.currentChat?.meta?.assignee;
      }
      return this.$store.getters['contacts/getContact'](this.contactId);
    },
    contactAdditionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    contactId() {
      return this.currentChat.meta?.sender?.id;
    },
    currentConversationMetaData() {
      return this.$store.getters[
        'conversationMetadata/getConversationMetadata'
      ](this.conversationId);
    },
    hasContactAttributes() {
      const { custom_attributes: customAttributes } = this.contact;
      return customAttributes && Object.keys(customAttributes).length;
    },
    enableCrm() {
      const account = this.getAccount(this.currentAccountId);
      return account.enable_crm;
    },
  },
  watch: {
    conversationId(newConversationId, prevConversationId) {
      if (newConversationId && newConversationId !== prevConversationId) {
        // Evita duplicação de chamadas - contactId já vai disparar
        // this.getContactDetails();
      }
    },
    contactId(newContactId, prevContactId) {
      if (newContactId && newContactId !== prevContactId) {
        this.loadPanelData(newContactId);
      }
    },
  },
  mounted() {
    this.conversationSidebarItems = this.conversationSidebarItemsOrder;
    this.loadPanelData();
    // Clique no chip de sentimento (no compositor) abre a aba do Copiloto.
    bus.$on(BUS_EVENTS.OPEN_COPILOT, this.openCopilotTab);
  },
  beforeDestroy() {
    bus.$off(BUS_EVENTS.OPEN_COPILOT, this.openCopilotTab);
  },
  methods: {
    openCopilotTab() {
      if (this.isCopilotEnabled) this.activeTab = 'ai';
    },
    loadPanelData(contactId = this.contactId) {
      Promise.all([
        this.getContactDetails(),
        this.$store.dispatch('attributes/get', 0),
        this.$store.dispatch('attributes/getGroupAttributes'),
        contactId
          ? this.$store.dispatch('contactConversations/get', contactId)
          : Promise.resolve(),
      ]);
    },
    onClickAssignWorkplace(selectedItem) {
        const { id } = this.contact;
        let updatedFields
      if(this.contact.workplace == selectedItem.id){
        updatedFields = {
        id,
        workplace: null,
      };
      } else{
        updatedFields = {
        id,
        workplace: selectedItem.id ,
      };
      }
      this.$store.dispatch('contacts/update', updatedFields);

    },
    onPanelToggle() {
      this.onToggle();
    },
    getContactDetails() {
      if (this.contactId) {
        this.$store.dispatch('contacts/show', { id: this.contactId });
      }
    },
    getAttributesByModel() {
      if (this.contactId) {
        this.$store.dispatch('contacts/show', { id: this.contactId });
      }
    },
    openTranscriptModal() {
      this.showTranscriptModal = true;
    },

    onDragEnd() {
      this.dragging = false;
      this.updateUISettings({
        conversation_sidebar_items_order: this.conversationSidebarItems,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/variables';

.contact--panel {
  background: var(--color-surface);
  border-left: 1px solid var(--color-border);
  font-size: $font-size-small;
  overflow-y: auto;
  overflow: auto;
  position: relative;

  i {
    margin-right: $space-smaller;
  }
}

.ctx-tabs {
  display: flex;
  align-items: center;
  gap: var(--space-two);
  padding: 0 var(--space-normal);
  border-bottom: 1px solid var(--color-border);
  position: sticky;
  top: 0;
  background: var(--color-surface);
  z-index: 2;
}

.ctx-collapse {
  margin-left: auto;
  width: 30px;
  height: 30px;
  border-radius: var(--border-radius-normal);
  display: grid;
  place-items: center;
  color: var(--s-500);
  cursor: pointer;
  transition: background 0.15s ease-in-out, color 0.15s ease-in-out;

  &:hover {
    background: var(--s-50);
    color: var(--s-800);
  }
}

.ctx-tab {
  display: inline-flex;
  align-items: center;
  gap: var(--space-smaller);
  padding: var(--space-slab) 0;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-bold);
  color: var(--s-500);
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  cursor: pointer;
  transition: color 0.15s ease-in-out, border-color 0.15s ease-in-out;

  &:hover {
    color: var(--s-800);
  }

  &.active {
    color: var(--color-woot);
    border-bottom-color: var(--color-woot);
  }

  &--ai.active {
    color: #7a5cf0;
    border-bottom-color: #7a5cf0;
  }
}

.ctx-tab-ic {
  width: 15px;
  height: 15px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.ai-copilot {
  padding: var(--space-normal);
}

.ai-card {
  background: linear-gradient(160deg, #faf8ff, #f2f6ff);
  border: 1px solid #e6dffb;
  border-radius: var(--border-radius-large);
  padding: var(--space-normal);

  h5 {
    display: flex;
    align-items: center;
    gap: var(--space-smaller);
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-bold);
    color: #7a5cf0;
    margin-bottom: var(--space-small);
  }

  .ai-card--body {
    font-size: var(--font-size-small);
    color: var(--s-600);
    line-height: 1.5;
    margin-bottom: var(--space-small);
  }
}

.ai-tag--soon {
  display: inline-flex;
  align-items: center;
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-bold);
  color: #7a5cf0;
  background: #f0ecfe;
  padding: var(--space-micro) var(--space-small);
  border-radius: 20px;
}

.group--attribute{
  margin: 15px;
}

.list-group {
  .list-group-item {
    background-color: var(--color-surface);
  }
}

::v-deep {
  .contact--profile {
    padding-bottom: var(--space-slab);
    border-bottom: 1px solid var(--color-border);
  }
  .conversation--actions .multiselect-wrap--small {
    .multiselect {
      padding-left: var(--space-medium);
      box-sizing: border-box;
    }
    .multiselect__element {
      span {
        width: 100%;
      }
    }
  }
}

.conversation--labels {
  padding: $space-medium;

  .icon {
    margin-right: $space-micro;
    font-size: $font-size-micro;
    color: #fff;
  }

  .label {
    color: #fff;
    padding: 0.2rem;
  }
}

.contact--mute {
  color: $alert-color;
  display: block;
  text-align: left;
}

.contact--actions {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.contact-info {
  margin-top: var(--space-two);
}
</style>
